import type { ChessBoard, ChessPosition, ChessColor } from '../../types/chess';
import type { MoveValidationContext, MoveGenerationContext } from './types';

// Core utility functions
export function isValidPosition(row: number, col: number): boolean {
  return row >= 0 && row < 8 && col >= 0 && col < 8;
}

export function findKing(board: ChessBoard, color: ChessColor): ChessPosition | null {
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = board[row][col];
      if (piece?.type === 'king' && piece.color === color) {
        return { row, col };
      }
    }
  }
  return null;
}

export function makeMove(board: ChessBoard, from: ChessPosition, to: ChessPosition): ChessBoard {
  const newBoard = board.map(row => [...row]);
  newBoard[to.row][to.col] = newBoard[from.row][from.col];
  newBoard[from.row][from.col] = null;
  return newBoard;
}

// Move generation functions
export function generatePawnMoves({ board, row, col, color, moves }: MoveGenerationContext) {
  const direction = color === 'white' ? -1 : 1;
  const startRow = color === 'white' ? 6 : 1;

  if (isValidPosition(row + direction, col) && !board[row + direction][col]) {
    moves.push({ row: row + direction, col });
    
    if (row === startRow && !board[row + direction * 2]?.[col]) {
      moves.push({ row: row + direction * 2, col });
    }
  }

  [-1, 1].forEach(offset => {
    const newRow = row + direction;
    const newCol = col + offset;
    if (isValidPosition(newRow, newCol)) {
      const target = board[newRow][newCol];
      if (target && target.color !== color) {
        moves.push({ row: newRow, col: newCol });
      }
    }
  });
}

export function generateSlidingMoves(
  { board, row, col, color, moves }: MoveGenerationContext,
  directions: number[][]
) {
  directions.forEach(([rowDir, colDir]) => {
    let newRow = row + rowDir;
    let newCol = col + colDir;
    
    while (isValidPosition(newRow, newCol)) {
      const target = board[newRow][newCol];
      if (!target) {
        moves.push({ row: newRow, col: newCol });
      } else {
        if (target.color !== color) {
          moves.push({ row: newRow, col: newCol });
        }
        break;
      }
      newRow += rowDir;
      newCol += colDir;
    }
  });
}

export function generateKnightMoves({ board, row, col, color, moves }: MoveGenerationContext) {
  const offsets = [[-2, -1], [-2, 1], [-1, -2], [-1, 2], [1, -2], [1, 2], [2, -1], [2, 1]];
  
  offsets.forEach(([rowOffset, colOffset]) => {
    const newRow = row + rowOffset;
    const newCol = col + colOffset;
    
    if (isValidPosition(newRow, newCol)) {
      const target = board[newRow][newCol];
      if (!target || target.color !== color) {
        moves.push({ row: newRow, col: newCol });
      }
    }
  });
}

export function generateKingMoves({ board, row, col, color, moves }: MoveGenerationContext) {
  const offsets = [[-1, -1], [-1, 0], [-1, 1], [0, -1], [0, 1], [1, -1], [1, 0], [1, 1]];
  
  offsets.forEach(([rowOffset, colOffset]) => {
    const newRow = row + rowOffset;
    const newCol = col + colOffset;
    
    if (isValidPosition(newRow, newCol)) {
      const target = board[newRow][newCol];
      if (!target || target.color !== color) {
        moves.push({ row: newRow, col: newCol });
      }
    }
  });
}